import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

export interface ContentRect {
  width: number;
  height: number;
  top: number;
  right: number;
  left: number;
  bottom: number;
}

/**
 * Subscribes a ResizeObserver to `ref.current` and returns the latest `contentRect`.
 * Re-attaches when the ref target changes (e.g. child mounts after `ref` was null on first paint).
 */
export function useMeasureDirty(ref: RefObject<HTMLElement>): ContentRect {
  const frame = useRef(0);
  const lastObserved = useRef<HTMLElement | null>(null);
  const [rect, set] = useState({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });

  const [observer] = useState(
    () =>
      new ResizeObserver((entries) => {
        const entry = entries[0];

        if (entry) {
          cancelAnimationFrame(frame.current);

          frame.current = requestAnimationFrame(() => {
            if (ref.current) {
              set(entry.contentRect);
            }
          });
        }
      }),
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (el === lastObserved.current) {
      return;
    }
    observer.disconnect();
    lastObserved.current = el;
    if (el) {
      observer.observe(el);
    }
  });

  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test') {
    return {
      width: 1000,
    } as ContentRect;
  }

  return rect;
}
